import { Box, Button } from "@mui/material";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { selectAllCustomerContracts } from "../customerContractsSlice";
import ContractForm from "./ContractForm";
import { ContractsTabModalTypes } from "./constants";
import { handlePricingData } from "./helpers/pricingUtils";
import EditExistingPricingForm from "./pricing/EditExistingPricingForm";
import NewPricingFromExistingDataForm from "./pricing/NewPricingFromExistingDataForm";
import NewPricingNoExistingDataForm from "./pricing/NewPricingNoExistingDataForm";
import PricingList from "./pricing/PricingList";
import PricingPerTesForm from "./pricing/PricingPerTesForm";
import PricingGeneralSettingsForm from "./pricing/form/generalSettings/PricingGeneralSettingsForm";
import { AddPricingButton } from "./styles";
import useIsLoading from "../../../hooks/useIsLoading";
import ContractDataProvider from "./ContractDataProvider";

export interface OpenModalParams {
  type: ContractsTabModalTypes;
  data?: any;
}

const ContractsTabContent = () => {
  const { t } = useTranslation("customers");
  const [searchParams] = useSearchParams();
  const eezyCompanyId = searchParams.get("ecom");

  const { canEditContract } = useContext(AccessRightContext);
  const canEdit = !!eezyCompanyId && canEditContract(eezyCompanyId);

  const isLoading = useIsLoading(["customerContracts"]);

  const customerContract = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  ).find((contract) => contract.eezyCompany.id === eezyCompanyId);

  // BE takes care of access checking (including 'nationwide' customer cases). Most of the
  // data is omitted when user has no read access; check the presence of 'version' field.
  const canRead = !!eezyCompanyId && customerContract?.version;
  const pricingsObj = customerContract?.pricing;
  const pricings = pricingsObj ? handlePricingData(pricingsObj) : [];

  const [modal, setModal] = useState<{
    type: string | null;
    isOpen: boolean;
    data?: any;
  }>({ type: null, isOpen: false, data: null });

  const handleOpenModal = ({ type, data }: { type: string; data?: object }) => {
    setModal({ type, isOpen: true, data });
  };

  const handleCloseModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const onModalExited = () => {
    setModal({ type: null, isOpen: false, data: null });
  };

  const eezyContact = customerContract?.salesPersonName;
  const contractButtonText = eezyContact
    ? `${t("contract.showContract")} (${t(
        "contract.eezyContact",
      )}: ${eezyContact})`
    : `${t("contract.showContract")}`;

  const pricingExists = pricings.length > 0;
  const existingPricingValidFromDates = pricings.map(
    (pricing) => pricing.pricingValidFromDate,
  );

  const getModalData = () => {
    if (pricingExists && modal.data) return modal.data;
    return {}; // new item
  };

  const getModalContent = () => {
    switch (modal.type) {
      case ContractsTabModalTypes.Contract:
        return (
          <ContractDataProvider>
            <ContractForm
              contractData={customerContract}
              closeDialog={() => handleCloseModal()}
            />
          </ContractDataProvider>
        );
      case ContractsTabModalTypes.NewPricingNoExistingData:
        return (
          <NewPricingNoExistingDataForm
            contractData={customerContract}
            eezyCompanyName={customerContract.eezyCompany.name}
            closeModal={() => handleCloseModal()}
          />
        );
      case ContractsTabModalTypes.NewPricingFromExistingData:
        return (
          <NewPricingFromExistingDataForm
            contractData={customerContract}
            pricingData={pricings}
            existingPricingValidFromDates={existingPricingValidFromDates}
            eezyCompanyName={customerContract.eezyCompany.name}
            closeModal={() => handleCloseModal()}
          />
        );
      case ContractsTabModalTypes.EditExistingPricing:
        return (
          <EditExistingPricingForm
            contractData={customerContract}
            pricingData={getModalData()}
            eezyCompanyName={customerContract.eezyCompany.name}
            closeModal={() => handleCloseModal()}
          />
        );
      case ContractsTabModalTypes.PricingPerTes:
        return (
          <PricingPerTesForm
            contractData={customerContract}
            pricingPerTesData={getModalData()}
            eezyCompanyName={customerContract.eezyCompany.name}
            closeModal={() => handleCloseModal()}
          />
        );
      case ContractsTabModalTypes.PricingGeneralSettings:
        return (
          <PricingGeneralSettingsForm
            contractData={customerContract}
            pricingGeneralSettingsData={getModalData()}
            eezyCompanyName={customerContract.eezyCompany.name}
            closeModal={() => handleCloseModal()}
          />
        );
      default:
        return <></>;
    }
  };

  const getCustomModalStyles = () => {
    const isAltModal = [
      ContractsTabModalTypes.NewPricingNoExistingData,
      ContractsTabModalTypes.NewPricingFromExistingData,
      ContractsTabModalTypes.EditExistingPricing,
    ].includes(modal.type as ContractsTabModalTypes);

    const isCardDialog = [
      ContractsTabModalTypes.Contract,
      ContractsTabModalTypes.PricingPerTes,
      ContractsTabModalTypes.PricingGeneralSettings,
    ].includes(modal.type as ContractsTabModalTypes);

    if (isCardDialog) {
      return {
        sx: {
          maxWidth: "963px",
        },
      };
    }

    if (!isAltModal) return {};

    // Custom styles for alternative modals
    return {
      sx: {
        width: "548px",
        backgroundColor: "#FFFFFF",
      },
    };
  };

  const getModalVariant = () => {
    switch (modal.type) {
      case ContractsTabModalTypes.Contract:
      case ContractsTabModalTypes.PricingPerTes:
      case ContractsTabModalTypes.PricingGeneralSettings:
        return { variant: "card" as "card" };
      default:
        return { variant: "form" as "form" };
    }
  };

  return (
    <Box>
      <OverlaySpinner isLoading={isLoading} />
      <DialogComponent
        {...getCustomModalStyles()}
        {...getModalVariant()}
        open={modal.isOpen}
        onClose={() => handleCloseModal()}
        TransitionProps={{ onExited: onModalExited }}
      >
        {getModalContent()}
      </DialogComponent>
      {customerContract && (
        <Button
          sx={{ margin: "0.5em" }}
          disabled={!canRead}
          onClick={() =>
            handleOpenModal({
              type: ContractsTabModalTypes.Contract,
            })
          }
        >
          {contractButtonText}
        </Button>
      )}
      <Box>
        <AddPricingButton
          accessRightChecks={[canEdit]}
          variant="contained"
          label={t("contract.pricing")}
          onClick={() =>
            handleOpenModal({
              type: pricingExists
                ? ContractsTabModalTypes.NewPricingFromExistingData
                : ContractsTabModalTypes.NewPricingNoExistingData,
            })
          }
        />
      </Box>
      <PricingList
        pricingExists={pricingExists}
        pricings={pricings}
        pricingsObj={pricingsObj}
        existingPricingValidFromDates={existingPricingValidFromDates}
        handleOpenModal={handleOpenModal}
        canEdit={canEdit}
        canRead={canRead}
      />
    </Box>
  );
};

export default ContractsTabContent;
